import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { api, extractApiError } from '@/lib/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

// Mirror of backend password_policy_violations — keep in sync with
// app/services/password.py (MIN_LENGTH=12, 3-of-4 char classes).
const resetSchema = z
  .object({
    password: z.string().min(12, 'At least 12 characters'),
    confirm: z.string().min(1, 'Required'),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type ResetValues = z.infer<typeof resetSchema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const form = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
  });

  const mutation = useMutation({
    mutationFn: async (values: ResetValues) => {
      if (!token) throw new Error('Missing token');
      await api.post('/auth/reset-password', {
        token,
        password: values.password,
      });
    },
    onSuccess: () => {
      // Land on sign-in so the user logs in with the new password.
      setTimeout(() => navigate('/sign-in', { replace: true }), 2500);
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'auth.reset_token_invalid') {
        toast.error('This reset link is invalid or expired. Request a new one.');
      } else if (apiErr?.code === 'auth.password_policy') {
        const violations = (apiErr.metadata?.violations ?? []) as string[];
        toast.error(`Password rejected: ${violations.join(', ') || 'see policy'}`);
      } else if (apiErr?.code === 'auth.rate_limited') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.error(apiErr?.message || 'Could not reset password. Please try again.');
      }
    },
  });

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Missing token
          </CardTitle>
          <CardDescription>
            This page must be opened from a password reset link in your email.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (mutation.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Password updated
          </CardTitle>
          <CardDescription>
            Your password has been reset. Redirecting you to sign in…
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild className="w-full">
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const onSubmit = (values: ResetValues) => {
    mutation.mutate(values);
  };

  const isPending = mutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Set a new password</CardTitle>
        <CardDescription>
          Choose a strong password — minimum 12 characters with a mix of letters, digits, and
          symbols.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="********"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="********"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? 'Updating…' : 'Set new password'}
            </Button>
            <div className="text-sm text-muted-foreground">
              <Link to="/sign-in" className="hover:underline">
                Back to sign in
              </Link>
            </div>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
